import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ArrowRightOnRectangleIcon, CheckIcon, UserCircleIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useSignOut } from '../../hooks/useSignOut';
import { setTheme, ThemeChoice, useTheme } from '../../lib/theme';
import type { RootState } from '../../store';

const ITEM = 'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-small text-strong data-[focus]:bg-hover';

const THEMES: ThemeChoice[] = ['system', 'light', 'dark'];

const useUserLabel = () => {
    const user = useSelector((s: RootState) => s.auth.user);
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
    const initials = (user?.firstName && user?.lastName ? user.firstName[0] + user.lastName[0] : name.slice(0, 2)).toUpperCase();
    return { name, initials, role: user?.role?.name };
};

const Avatar = ({ initials }: { initials: string }) => (
    <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-chip bg-sunken text-caption font-semibold text-strong">
        {initials}
    </span>
);

/** Profile, language and sign out. `row` is the sidebar footer (opens upward), `avatar` the top bar button. */
export const UserMenu = ({ variant }: { variant: 'avatar' | 'row' }) => {
    const { t } = useTranslation(['common', 'team']);
    const { name, initials, role } = useUserLabel();
    const { code, languages, change } = useLanguage();
    const signOut = useSignOut();
    const { choice } = useTheme();

    return (
        <Menu>
            {variant === 'avatar' ? (
                <MenuButton
                    // WCAG 2.5.3: the accessible name starts with the visible initials.
                    aria-label={`${initials}, ${t('shell.userMenu', { name })}`}
                    className="relative flex size-11 items-center justify-center rounded-chip transition-transform duration-hover ease-snap active:scale-[0.97]"
                >
                    <Avatar initials={initials} />
                </MenuButton>
            ) : (
                <MenuButton className="flex h-14 w-full items-center gap-3 rounded-control px-3 text-left transition-colors duration-hover ease-snap hover:bg-hover">
                    <Avatar initials={initials} />
                    <span className="min-w-0">
                        <span className="block truncate text-small font-semibold text-strong">{name}</span>
                        <span className="block text-caption text-muted">{role && t(`team:roles.${role}`, { defaultValue: role })}</span>
                    </span>
                </MenuButton>
            )}
            <MenuItems
                anchor={variant === 'row' ? 'top start' : 'bottom end'}
                transition
                className="z-40 w-60 rounded-control bg-raised p-1 shadow-e2 outline-none transition duration-hover ease-snap [--anchor-gap:4px] data-[closed]:opacity-0"
            >
                <MenuItem>
                    <Link to="/profile" className={ITEM}>
                        <UserCircleIcon className="size-5" aria-hidden="true" />
                        {t('nav.profile')}
                    </Link>
                </MenuItem>
                <div role="separator" className="my-1 h-px bg-line" />
                <p className="px-3 pb-1 pt-2 text-caption text-muted" id="user-menu-language">
                    {t('shell.language')}
                </p>
                {languages.map((l) => (
                    <MenuItem key={l.code}>
                        <button type="button" aria-current={l.code === code ? 'true' : undefined} lang={l.code} onClick={() => change(l.code)} className={clsx(ITEM, 'justify-between')}>
                            {l.label}
                            {l.code === code && <CheckIcon className="size-5" aria-hidden="true" />}
                        </button>
                    </MenuItem>
                ))}
                <div role="separator" className="my-1 h-px bg-line" />
                <p className="px-3 pb-1 pt-2 text-caption text-muted">{t('theme.label')}</p>
                {THEMES.map((v) => (
                    <MenuItem key={v}>
                        <button type="button" aria-current={v === choice ? 'true' : undefined} onClick={() => setTheme(v)} className={clsx(ITEM, 'justify-between')}>
                            {t(`theme.${v}`)}
                            {v === choice && <CheckIcon className="size-5" aria-hidden="true" />}
                        </button>
                    </MenuItem>
                ))}
                <div role="separator" className="my-1 h-px bg-line" />
                <MenuItem>
                    <button type="button" onClick={() => void signOut()} className={ITEM}>
                        <ArrowRightOnRectangleIcon className="size-5" aria-hidden="true" />
                        {t('shell.signOut')}
                    </button>
                </MenuItem>
            </MenuItems>
        </Menu>
    );
};
